import { useNavigate } from 'react-router-dom';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetFooter } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { useCart } from '@/contexts/CartContext';
import { ShoppingCart, Trash2, Calendar, DollarSign, ArrowRight, Info, Check } from 'lucide-react';
import { format } from 'date-fns';
import { parseDateOnly } from '@/lib/utils';
import { isDeferredFirstCharge, getBillingSummary } from '@/lib/billing-display';
import { getCartPaymentOptions, getCartLineAmount, getCartLineDueToday } from '@/lib/cartPaymentOptions';

const CartDrawer = () => {
  const navigate = useNavigate();
  const {
    items,
    removeFromCart,
    clearCart,
    cartTotal,
    hasPendingPaymentSelection,
    setPaymentOption,
    isCartOpen,
    setIsCartOpen,
  } = useCart();

  const handleCheckout = () => {
    if (hasPendingPaymentSelection) return;
    setIsCartOpen(false);
    navigate('/checkout');
  };


  return (
    <Sheet open={isCartOpen} onOpenChange={setIsCartOpen}>
      <SheetContent className="w-full sm:max-w-md h-[100dvh] max-h-[100dvh] flex flex-col pb-[calc(env(safe-area-inset-bottom)+1.5rem)]">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2 font-display text-camp-forest">
            <ShoppingCart className="h-5 w-5" />
            Registration Cart
            {items.length > 0 && (
              <Badge variant="secondary" className="ml-2">
                {items.length} {items.length === 1 ? 'program' : 'programs'}
              </Badge>
            )}
          </SheetTitle>
        </SheetHeader>

        {items.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center text-center py-12">
            <ShoppingCart className="h-16 w-16 text-muted-foreground/30 mb-4" />
            <h3 className="font-semibold text-lg mb-2">No programs selected</h3>
            <p className="text-muted-foreground text-sm mb-6">
              Browse our programs and click "Register Now" to get started.
            </p>
            <Button 
              onClick={() => {
                setIsCartOpen(false);
                navigate('/programs');
              }}
              className="bg-primary text-primary-foreground border border-primary hover:bg-background hover:text-primary hover:border-primary"
            >
              Browse Programs
            </Button>
          </div>
        ) : (
          <>
            <ScrollArea className="flex-1 min-h-0 -mx-6 px-6">
              <div className="space-y-4 py-4">
                {items.map((item) => (
                  <div
                    key={item.program.id}
                    className="bg-muted/50 rounded-lg p-4 space-y-3"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <h4 className="font-semibold text-camp-forest truncate">
                          {item.program.name}
                        </h4>
                        {item.program.start_date && (
                          <div className="flex items-center gap-1.5 text-sm text-muted-foreground mt-1">
                            <Calendar className="h-3.5 w-3.5" />
                            <span>
                              {format(parseDateOnly(item.program.start_date), 'MMM d, yyyy')}
                            </span>
                          </div>
                        )}
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-muted-foreground hover:text-destructive"
                        onClick={() => removeFromCart(item.program.id)}
                        aria-label="Remove from cart"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>

                    {(() => {
                      const options = getCartPaymentOptions(item.program);
                      const selected = item.paymentOption;
                      const perChild = getCartLineAmount(item.program, selected);
                      const multi = options.length > 1;

                      return (
                        <>
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-1.5 font-medium text-camp-forest">
                              <DollarSign className="h-4 w-4" />
                              {!selected && multi ? (
                                <span className="text-sm font-semibold text-muted-foreground">
                                  Pending — choose a plan
                                </span>
                              ) : perChild === null ? (
                                <span className="text-sm font-semibold">Based on attendance</span>
                              ) : (
                                <>
                                  <span>${perChild.toFixed(2)}</span>
                                  {selected === 'recurring' && (
                                    <span className="text-sm text-muted-foreground font-normal">/ month</span>
                                  )}
                                  {(selected === 'deposit' || selected === 'payment_plan') && (
                                    <span className="text-sm text-muted-foreground font-normal">due today</span>
                                  )}
                                </>
                              )}
                            </div>
                            {item.coupon && (
                              <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                                {item.coupon.code}
                              </Badge>
                            )}
                          </div>

                          {multi && (
                            <div className="space-y-2">
                              <p className="text-xs font-medium text-camp-forest">
                                How would you like to pay?
                              </p>
                              <div className="grid gap-2">
                                {options.map(opt => {
                                  const active = selected === opt.value;
                                  const dueTodayText =
                                    opt.value === 'payg'
                                      ? '$0.00 due today'
                                      : opt.dueTodayPerChild === null
                                        ? 'Varies'
                                        : opt.dueTodayPerChild === 0
                                          ? '$0.00 due today'
                                          : `$${opt.dueTodayPerChild.toFixed(2)} due today`;
                                  return (
                                    <button
                                      key={opt.value}
                                      type="button"
                                      onClick={() => setPaymentOption(item.program.id, opt.value)}
                                      aria-pressed={active}
                                      className={`w-full text-left rounded-md border px-3 py-2 transition-all duration-150 active:scale-[0.99] ${
                                        active
                                          ? 'border-camp-grass bg-camp-grass/10 shadow-sm'
                                          : 'border-border bg-background hover:border-camp-grass/50 hover:bg-muted/60'
                                      }`}
                                    >
                                      <div className="flex items-center justify-between gap-2">
                                        <span className="text-sm font-semibold text-camp-forest">
                                          {opt.label}
                                        </span>
                                        <span className="flex items-center gap-1 text-xs font-medium text-muted-foreground">
                                          {dueTodayText}
                                          {active && <Check className="h-3.5 w-3.5 text-camp-grass" />}
                                        </span>
                                      </div>
                                      <p className="text-xs text-muted-foreground mt-0.5">{opt.hint}</p>
                                      {opt.laterNote && (
                                        <p className="text-xs text-camp-forest/80 mt-0.5">{opt.laterNote}</p>
                                      )}
                                    </button>
                                  );
                                })}
                              </div>
                            </div>
                          )}


                          {(() => {
                            if (!selected && multi) return null;
                            const summary = getBillingSummary(item.program, selected ?? null);
                            return (
                              <div
                                className={`flex items-start gap-2 rounded-md px-3 py-2 text-xs leading-snug ${
                                  summary.deferred
                                    ? 'bg-camp-sky/15 border border-camp-sky/30 text-camp-forest'
                                    : 'bg-muted/60 border border-border text-foreground'
                                }`}
                              >
                                <Info className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                                <div>
                                  <strong className="font-semibold">{summary.headline}</strong>
                                  {summary.detail && <div className="mt-0.5">{summary.detail}</div>}
                                </div>
                              </div>
                            );
                          })()}
                        </>
                      );
                    })()}

                  </div>
                ))}
              </div>
            </ScrollArea>

            <div className="shrink-0 pt-4 space-y-4">
              <Separator />

              {(() => {
                const allDeferred = items.every(i => isDeferredFirstCharge(i.program, i.paymentOption ?? undefined));
                const anyDeferred = items.some(i => isDeferredFirstCharge(i.program, i.paymentOption ?? undefined));
                const anyPayg = items.some(i => i.paymentOption === 'payg');
                const dueTodayTotal = items.reduce(
                  (sum, i) => sum + (getCartLineDueToday(i.program, i.paymentOption ?? undefined) ?? 0),
                  0,
                );
                const laterTotal = Math.max(0, cartTotal - dueTodayTotal);
                const laterNotes = items
                  .map(i => ({
                    name: i.program.name,
                    note: getBillingSummary(i.program, i.paymentOption ?? null).detail,
                  }))
                  .filter(n => !!n.note);

                return (
                  <>
                    <div className="flex items-center justify-between">
                      <span className="text-muted-foreground">
                        {hasPendingPaymentSelection ? 'Total' : 'Due today'}
                      </span>
                      {hasPendingPaymentSelection ? (
                        <span className="text-sm font-semibold text-muted-foreground">
                          Pending plan selection
                        </span>
                      ) : (
                        <span className="text-lg font-semibold text-camp-forest">
                          ${dueTodayTotal.toFixed(2)}
                        </span>
                      )}
                    </div>

                    {!hasPendingPaymentSelection && laterTotal > 0.005 && (
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-muted-foreground">Scheduled later</span>
                        <span className="font-medium text-camp-forest">
                          ${laterTotal.toFixed(2)}
                          {allDeferred && !anyPayg && (
                            <span className="text-xs font-normal text-muted-foreground ml-1">/ mo</span>
                          )}
                        </span>
                      </div>
                    )}

                    {!hasPendingPaymentSelection && laterNotes.length > 0 && (
                      <ul className="space-y-1">
                        {laterNotes.map(n => (
                          <li key={n.name} className="text-xs text-muted-foreground leading-snug">
                            <span className="font-medium text-camp-forest">{n.name}:</span> {n.note}
                          </li>
                        ))}
                      </ul>
                    )}

                    {hasPendingPaymentSelection ? (
                      <p className="text-sm text-muted-foreground leading-relaxed">
                        Choose a payment plan above for each program to see your total.
                      </p>
                    ) : anyPayg ? (
                      <p className="text-sm text-muted-foreground leading-relaxed">
                        <strong className="text-camp-forest">You won't be charged today.</strong> Pay-as-you-go programs are invoiced monthly based on actual attendance.
                      </p>
                    ) : allDeferred ? (
                      <p className="text-sm text-muted-foreground leading-relaxed">
                        <strong className="text-camp-forest">You won't be charged today.</strong> Your card will be saved during checkout and charged on each program's first billing date.
                      </p>
                    ) : anyDeferred ? (
                      <p className="text-sm text-muted-foreground leading-relaxed">
                        Some programs charge today; recurring programs save your card and charge on their first billing date.
                      </p>
                    ) : (
                      <p className="text-sm text-muted-foreground leading-relaxed">
                        You'll see the full payment schedule before you confirm at checkout.
                      </p>
                    )}

                    <SheetFooter className="flex-col gap-2 sm:flex-col">
                      <Button
                        onClick={handleCheckout}
                        disabled={hasPendingPaymentSelection}
                        className="w-full bg-gradient-to-r from-camp-sun to-camp-grass hover:from-camp-sun/90 hover:to-camp-grass/90 text-white font-bold shadow-lg hover:shadow-xl transition-all duration-200 text-base"
                        size="lg"
                      >
                        {hasPendingPaymentSelection
                          ? 'Choose a payment plan'
                          : `Continue to Checkout · $${dueTodayTotal.toFixed(2)} due today`}
                        {!hasPendingPaymentSelection && <ArrowRight className="ml-2 h-5 w-5" />}
                      </Button>
                    </SheetFooter>
                  </>
                );
              })()}

              <Button
                variant="ghost"
                onClick={clearCart}
                className="w-full text-muted-foreground hover:text-destructive"
              >
                Clear All
              </Button>
            </div>

          </>
        )}
      </SheetContent>
    </Sheet>
  );
};

export default CartDrawer;
